import React from 'react';
import usePageMeta from '../usePageMeta';
import { LEGAL } from '../siteData';
import { useContact } from '../SiteContent';

/**
 * Angaben nach § 5 DDG (bis 2024: § 5 TMG) und § 18 Abs. 2 MStV.
 *
 * Die Stammdaten stehen in `siteData.ts` unter LEGAL. Fehlende Pflichtangaben
 * werden hier sichtbar als offen gekennzeichnet statt weggelassen — ein
 * unvollständiges Impressum, das vollständig aussieht, ist die schlechtere
 * Variante. Optionale Angaben („soweit vorhanden") entfallen dagegen still.
 */

/** Kennzeichnet eine Pflichtangabe, die noch nachgetragen werden muss. */
const Offen: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <mark
    style={{
      background: '#fdf3d7',
      color: 'var(--ink)',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 14,
    }}
  >
    {children}
  </mark>
);

const Impressum: React.FC = () => {
  const contact = useContact();

  usePageMeta(
    'Impressum',
    `Impressum der ${contact.company}, ${LEGAL.address.street}, ${LEGAL.address.zip} ${LEGAL.address.city}.`,
  );

  return (
    <section className="shell shell--prose" style={{ paddingTop: 64, paddingBottom: 60 }}>
      <h1 className="h-page" style={{ marginTop: 0 }}>
        Impressum
      </h1>

      <h2 className="h-card" style={{ marginTop: 32 }}>
        Angaben gemäß § 5 DDG
      </h2>
      <p style={{ lineHeight: 1.8 }}>
        {contact.company}
        <br />
        Inhaber: {LEGAL.owner}
        <br />
        {LEGAL.address.street}
        <br />
        {LEGAL.address.zip} {LEGAL.address.city}
        <br />
        Deutschland
      </p>
      <p style={{ lineHeight: 1.8 }}>
        Rechtsform: {LEGAL.legalForm}
        {LEGAL.registerCourt && LEGAL.registerNumber && (
          <>
            <br />
            Registergericht: {LEGAL.registerCourt}
            <br />
            Registernummer: {LEGAL.registerNumber}
          </>
        )}
      </p>

      <h2 className="h-card" style={{ marginTop: 32 }}>
        Kontakt
      </h2>
      <p style={{ lineHeight: 1.8 }}>
        Telefon: <a href={contact.phoneHref}>{contact.phoneLabel}</a>
        <br />
        E-Mail: <a href={contact.emailHref}>{contact.email}</a>
      </p>

      <h2 className="h-card" style={{ marginTop: 32 }}>
        Umsatzsteuer
      </h2>
      <p style={{ lineHeight: 1.8 }}>
        {LEGAL.vatId ? (
          <>
            Umsatzsteuer-Identifikationsnummer gemäß § 27 a UStG:
            <br />
            {LEGAL.vatId}
          </>
        ) : (
          <Offen>
            Umsatzsteuer-Identifikationsnummer noch zu prüfen und zu ergänzen — falls keine
            erteilt wurde, entfällt dieser Abschnitt ersatzlos.
          </Offen>
        )}
      </p>

      {LEGAL.institutionsKennzeichen && (
        <>
          <h2 className="h-card" style={{ marginTop: 32 }}>
            Institutionskennzeichen
          </h2>
          <p style={{ lineHeight: 1.8 }}>
            IK {LEGAL.institutionsKennzeichen}
            <br />
            <span className="note">
              Kennung für die Abrechnung mit den Pflege- und Krankenkassen.
            </span>
          </p>
        </>
      )}

      {/*
        Nur wenn es etwas zu nennen gibt. § 5 Abs. 1 Nr. 3 DDG verlangt die
        Aufsichtsbehörde bei zulassungspflichtigen Tätigkeiten — Hauswirtschaft
        und Alltagsbegleitung gehören nicht dazu (Begründung an LEGAL).
      */}
      {LEGAL.supervisoryAuthority && (
        <>
          <h2 className="h-card" style={{ marginTop: 32 }}>
            Zuständige Aufsichtsbehörde
          </h2>
          <p style={{ lineHeight: 1.8 }}>
            {LEGAL.supervisoryAuthority}
            <br />
            <span className="note">
              Anerkennung als Angebot zur Unterstützung im Alltag nach § 45 a SGB XI.
            </span>
          </p>
        </>
      )}

      <h2 className="h-card" style={{ marginTop: 32 }}>
        Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV
      </h2>
      <p style={{ lineHeight: 1.8 }}>
        {LEGAL.owner}
        <br />
        {LEGAL.address.street}, {LEGAL.address.zip} {LEGAL.address.city}
      </p>

      <h2 className="h-card" style={{ marginTop: 32 }}>
        Verbraucherstreitbeilegung
      </h2>
      <p style={{ lineHeight: 1.8 }}>
        Wir sind nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer
        Verbraucherschlichtungsstelle teilzunehmen.
      </p>

      <h2 className="h-card" style={{ marginTop: 32 }}>
        Haftung für Inhalte und Links
      </h2>
      <p style={{ lineHeight: 1.8 }}>
        Die Inhalte dieser Seiten haben wir mit Sorgfalt erstellt. Für ihre Richtigkeit,
        Vollständigkeit und Aktualität können wir jedoch keine Gewähr übernehmen. Unser Angebot
        enthält Links zu Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Für diese
        fremden Inhalte ist stets der jeweilige Anbieter verantwortlich. Rechtswidrige Inhalte
        waren zum Zeitpunkt der Verlinkung nicht erkennbar; werden uns Rechtsverletzungen bekannt,
        entfernen wir die betreffenden Links umgehend.
      </p>

      <p className="note" style={{ marginTop: 40 }}>
        Stand: {LEGAL.lastUpdated}
      </p>
    </section>
  );
};

export default Impressum;
